import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { updateFavorites } from "./favorites/actions";
import { updateSessionInfo } from "./session/actions";
import { updateShoppingCart } from "./shoppingCart/actions";

export const verwimpApi = createApi({
  reducerPath: "verwimpApi",
  baseQuery: fetchBaseQuery({
    baseUrl: Craft.getSiteUrl(),
    prepareHeaders: (headers, { type }) => {
      headers.set("accept", "application/json");

      if (type === "mutation") {
        const csrfToken = window.Verwimp.csrf;
        if (csrfToken) {
          headers.set("X-CSRF-Token", csrfToken);
        }
      }
      return headers;
    },
  }),
  tagTypes: ["shipments", "purchase-info", "cart", "back-in-stock"],
  endpoints: (builder) => ({
    getUserSessionInfo: builder.query({
      query: () => "actions/users/session-info",
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateSessionInfo(data));
      },
    }),
    getShoppingCart: builder.query({
      query: ({ cartType, ownerId, currentUserOnly }) =>
        `actions/verwimp-portal/cart/index?cartType=${cartType}&ownerId=${ownerId}&currentUserOnly=${currentUserOnly}`,
      providesTags: ["cart"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateShoppingCart(data));
      },
    }),
    getFavorites: builder.query({
      query: () => "actions/verwimp-portal/favorites/get",
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateFavorites(data));
      },
    }),
    addFavorite: builder.mutation({
      query: ({ elementId }) => {
        const body = new FormData();
        body.append("elementId", elementId);
        return {
          url: "actions/verwimp-portal/favorites/add",
          method: "POST",
          body,
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateFavorites(data));
      },
    }),
    removeFavorite: builder.mutation({
      query: ({ elementId }) => {
        const body = new FormData();
        body.append("elementId", elementId);
        return {
          url: "actions/verwimp-portal/favorites/remove",
          method: "POST",
          body,
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateFavorites(data));
      },
    }),
    updateCartItem: builder.mutation({
      query: (body) => ({
        url: "actions/verwimp-portal/cart/update-cart-item",
        method: "POST",
        body,
      }),
      invalidatesTags: ["cart"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateShoppingCart(data));
      },
    }),
    setSelectedCartType: builder.mutation({
      query: (body) => ({
        url: "actions/verwimp-portal/cart/set-selected-cart-type",
        method: "POST",
        body,
      }),
      invalidatesTags: ["cart"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(updateShoppingCart(data));
      },
    }),
    getOrderHistory: builder.query({
      query: ({ itemsPerPage = 10, filterText }) =>
        `actions/verwimp-portal/shipments/get?itemsPerPage=${itemsPerPage}&filterText=${filterText}`,
      providesTags: (result, error, arg) => [
        { type: "shipments", id: arg.filterText },
      ],
    }),
    getPurchaseInfo: builder.query({
      query: ({ productId }) =>
        `actions/verwimp-portal/products/purchase-info?productId=${productId}`,
      providesTags: (result, error, arg) => [
        { type: "purchase-info", id: arg.productId },
      ],
    }),
    getBackInStockSubscriptions: builder.query({
      query: () => "actions/verwimp-portal/back-in-stock/subscriptions",
      providesTags: ["back-in-stock"],
    }),
    subscribeBackInStock: builder.mutation({
      query: ({ elementId }) => {
        const body = new FormData();
        body.append("elementId", String(elementId));
        return {
          url: "actions/verwimp-portal/back-in-stock/subscribe",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["back-in-stock"],
    }),
    unsubscribeBackInStock: builder.mutation({
      query: ({ elementId }) => {
        const body = new FormData();
        body.append("elementId", String(elementId));
        return {
          url: "actions/verwimp-portal/back-in-stock/unsubscribe",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["back-in-stock"],
    }),
  }),
});

export const {
  util,
  useUpdateCartItemMutation,
  useSetSelectedCartTypeMutation,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
  useGetFavoritesQuery,
  useGetOrderHistoryQuery,
  useGetPurchaseInfoQuery,
  useGetBackInStockSubscriptionsQuery,
  useGetShoppingCartQuery,
  useLazyGetShoppingCartQuery,
  useLazyGetFavoritesQuery,
  useSubscribeBackInStockMutation,
  useUnsubscribeBackInStockMutation,
} = verwimpApi;
